/******************************************************************************************
 * This file holds the APIs for Penetapan by Kepala Dinas Pendidikan for Plt KS
 ******************************************************************************************/

import { apiPaths } from 'api/kinerja/kepala-sekolah/config'
import { useKinerjaApi, useKinerjaMutationApi } from 'api/kinerja/utils/api'

import type { CommonResponse } from 'types/api'
import type { PelaksanaanError } from 'types/kinerja-ks-pelaksanaan'
import type {
  TPayloadPenilaian,
  TPayloadPenilaianForDelivery,
  TPenilaianFilterStatus,
  TResponsePenilaian,
  TResponsePenilaianCount,
  TResponsePenilaianForDelivery,
  TResponsePenilaianSummary,
} from 'types/kinerja-dinas'
import type { UseMutationOptions, UseQueryOptions } from 'react-query'

type TPenilaianDinasProp = {
  name?: string
  status?: TPenilaianFilterStatus[]
  confirmedOnly?: boolean
}

export const API_GET_KS_PENILAIAN_BY_DINAS_TAB_GURU_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_DINAS_TAB_GURU_QUERY_KEY'

export const API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_TAB_GURU_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_TAB_GURU_QUERY_KEY'

export const API_GET_KS_PENILAIAN_COUNT_BY_DINAS_TAB_GURU_QUERY_KEY =
  'API_GET_KS_PENILAIAN_COUNT_BY_DINAS_TAB_GURU_QUERY_KEY'

export function useApiGetPenilaianDinasTabGuru(
  params: TPenilaianDinasProp,
  opts?: UseQueryOptions<TResponsePenilaian>
) {
  const nameParam = params?.name || undefined

  const status: TPenilaianFilterStatus[] = params?.status || ['ALL']
  const statusParam = status ? status?.join(',') : undefined

  // Intentionally exclude `false` from the query string so the API can handle it according to its default behavior
  const confirmedOnlyParam = params?.confirmedOnly === true || undefined

  return useKinerjaApi<TResponsePenilaian>(apiPaths.guruPenilaianPltKsDinas, {
    axiosConfig: {
      params: {
        name: nameParam,
        statusPengiriman: statusParam,
        confirmedOnly: confirmedOnlyParam,
      },
    },
    queryConfig: {
      useErrorBoundary: false,
      ...opts,
    },
    queryKey: [
      API_GET_KS_PENILAIAN_BY_DINAS_TAB_GURU_QUERY_KEY,
      nameParam,
      statusParam,
      confirmedOnlyParam,
    ],
  })
}

export function useApiGetPenilaianSummaryDinasTabGuru(
  opts?: UseQueryOptions<TResponsePenilaianSummary>
) {
  return useKinerjaApi<TResponsePenilaianSummary>(
    `${apiPaths.guruPenilaianPltKsDinas}/summary`,
    {
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_TAB_GURU_QUERY_KEY],
    }
  )
}

export function useApiGetPenilaianCountDinasTabGuru(
  opts?: UseQueryOptions<TResponsePenilaianCount>
) {
  return useKinerjaApi<TResponsePenilaianCount>(
    `${apiPaths.guruPenilaianPltKsDinas}/count`,
    {
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_COUNT_BY_DINAS_TAB_GURU_QUERY_KEY],
    }
  )
}

export function useApiPostPenilaianDinasForDeliveryTabGuru(
  opts?: UseMutationOptions<
    CommonResponse<TResponsePenilaianForDelivery>,
    PelaksanaanError,
    Pick<TPayloadPenilaianForDelivery, 'id'>
  >
) {
  return useKinerjaMutationApi(apiPaths.guruPenilaianPltKsDinas, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}

export function useApiPatchPenilaianDinasTabGuru(
  opts?: UseMutationOptions<
    CommonResponse<any>,
    PelaksanaanError,
    TPayloadPenilaian
  >
) {
  return useKinerjaMutationApi(`${apiPaths.guruPenilaianPltKsDinas}/override`, {
    axiosConfig: {
      method: 'PATCH',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}
